import React, { useEffect, useState, useContext, useMemo } from "react";
import HeaderSearch from "../../../../common/components/HeaderSearch";
import AssociatedClientsTable from "../../../associated-clients-table/components/AssociatedClientsTable";
import { LiferayService } from "../../../../common/services/liferay";
import { ApplicationPropertiesContext } from "../../../../common/context/ApplicationPropertiesProvider";
import { AccessTokenContext } from "../../../../common/context/AccessTokenProvider";
import AmiDetail from "../AmiDetail";

// Activar modo mock para evitar llamadas al backend
const USE_MOCK_PUNTOS = false;

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("es-CL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

// 3 filas mock mientras no haya selección
const MOCK_METERS = [
    /*{ serial: "AMI-000123", address: "AV FRANCISCO DE AGUIRRE S/N LA SERENA", validFrom: "2024-01-01", validTo: null },
    { serial: "AMI-000456", address: "LAS HIGUERAS 245, COQUIMBO", validFrom: "2024-07-15", validTo: "2025-01-30" },
    { serial: "AMI-000789", address: "ALAMEDA BERNARDO O’HIGGINS 123, SANTIAGO", validFrom: "2023-11-20", validTo: null },*/
];

export default function MedidoresAmiList() {
    const properties = useContext(ApplicationPropertiesContext);
    const { accessToken } = useContext(AccessTokenContext);

    const [ids, setIds] = useState([]);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState("");

    const [meters, setMeters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedRow, setSelectedRow] = useState(null);
    const [showDetail, setShowDetail] = useState(false);

    // 🔹 Labels leídos desde properties con fallback
    const labels = useMemo(() => {
        return {
            pageTitle: properties?.ami_page_title || "Mis Medidores AMI",
            colSerial: properties?.ami_column_serial || "Número de serie",
            colAddress: properties?.ami_column_address || "Dirección",
            colValidFrom: properties?.ami_column_valid_from || "Vigencia desde",
            colValidTo: properties?.ami_column_valid_to || "Vigencia hasta",
            loadingMeters: properties?.ami_loading_meters || "Cargando medidores AMI...",
            errorMeters:
                properties?.ami_error_meters ||
                "Ocurrió un error al consultar los medidores. Intenta nuevamente.",
            noMeters:
                properties?.ami_no_meters ||
                "No se encontraron medidores AMI para la cuenta seleccionada.",
        };
    }, [properties]);

    // === Cargar IDs para HeaderSearch ===
    useEffect(() => {
        if (!accessToken) return;

        LiferayService.getIdList(properties.base_url_api)
            .then((resp) => {
                const lista = Array.isArray(resp) ? resp : [];
                console.log("[AMI] IdList recibidos para HeaderSearch:", lista);
                setIds(lista);
            })
            .catch((err) => {
                console.error("[AMI] Error obteniendo IdList:", err);
                setIds([]);
            });
    }, [accessToken, properties.base_url_api]);

    // === Consultar PuntosSuministro cuando cambia selected ===
    useEffect(() => {
        console.log("[AMI] useEffect consultarPuntosSuministro disparado. selected =", selected);

        // MODO MOCK: no llamamos al backend, dejamos solo los mocks
        if (USE_MOCK_PUNTOS) {
            console.log("[AMI] MODO MOCK activo: se omite llamada a consultarPuntosSuministro.");
            return;
        }

        if (!selected) {
            console.log("[AMI] No hay cliente seleccionado, no se consulta aún.");
            setMeters([]);
            return;
        }

        if (!accessToken) {
            console.warn("[AMI] No accessToken, no se puede llamar al backend.");
            return;
        }

        //Cuenta contrato dinámica desde la selección
        const cuentaContrato = String(selected).trim();
        console.log("[AMI] Cuenta contrato utilizada para CPS:", cuentaContrato);

        const today = new Date();
        const fechaHasta = today.toISOString().slice(0, 10);

        const fromDate = new Date(today);
        fromDate.setFullYear(fromDate.getFullYear() - 2); // 24 meses antes
        const fechaDesde = fromDate.toISOString().slice(0, 10);

        const payload = {
            cuentaContrato,
            sociedad: properties.codigo_empresa || "",
            fechaDesde,
            fechaHasta,
            tipoMedidor: "AMI",
        };

        console.log("[AMI] >>> Llamando consultarPuntosSuministro");
        console.log("base_url_api:", properties.base_url_api);
        console.log("payload enviado:", payload);
        console.log("url_ws_empresa:", properties.url_ws_empresa);

        setLoading(true);
        setError(null);

        LiferayService.consultarPuntosSuministro(
            properties.base_url_api,
            payload,
            properties.url_ws_empresa
        )
            .then((resp) => {
                console.log("[AMI] Respuesta cruda del servicio:", resp);

                const data = resp?.data || resp || {};
                console.log("[AMI] Data normalizada:", data);
                console.log("[AMI] Respuesta backend (Respuesta):", data.Respuesta || data.respuesta);

                const rawPuntos = data.PuntosSuministro;

                let puntos = [];
                if (Array.isArray(rawPuntos)) {
                    puntos = rawPuntos;
                } else if (rawPuntos) {
                    puntos = [rawPuntos];
                }

                console.log(`[AMI] Total PuntosSuministro normalizados: ${puntos.length}`, puntos);

                const mapped = puntos.map((p) => ({
                    serial: p.mRID,
                    address: p.Direccion,
                    validFrom: p.FechaDesde,
                    validTo: p.FechaHasta,
                    raw: p,
                }));

                console.log("[AMI] Meters mapeados:", mapped);
                setMeters(mapped);
            })
            .catch((err) => {
                console.error("[AMI] ERROR consultando puntos de suministro:", err);
                setError(labels.errorMeters);
                setMeters([]);
            })
            .finally(() => {
                setLoading(false);
            });

    }, [
        selected,
        accessToken,
        properties.base_url_api,
        properties.url_ws_empresa,
        properties.codigo_empresa,
        labels.errorMeters,
    ]);

    const openDetail = (row) => {
        console.log("[AMI] Click en fila, abriendo detalle para:", row);
        setSelectedRow(row);
        setShowDetail(true);
    };

    const handleBack = () => {
        console.log("[AMI] Volviendo de detalle a lista.");
        setShowDetail(false);
        setSelectedRow(null);
    };

    // Si estamos en mock → siempre usar MOCK_METERS
    const effectiveMeters = useMemo(() => {
        if (USE_MOCK_PUNTOS) {
            return MOCK_METERS;
        }

        if (meters && meters.length > 0) {
            return meters;
        }

        if (!selected) {
            return MOCK_METERS;
        }

        return [];
    }, [meters, selected]);

    const titles = useMemo(
        () => [
            labels.colSerial,
            labels.colAddress,
            labels.colValidFrom,
            labels.colValidTo,
        ],
        [labels]
    );

    const currentArray = useMemo(() => {
        return effectiveMeters.map((m) => {
            const desde = formatDate(m.validFrom);
            const hasta = m.validTo ? formatDate(m.validTo) : "—";

            const Cell = ({ children }) => (
                <a
                    className="link"
                    role="button"
                    onClick={() => openDetail(m)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openDetail(m)}
                    tabIndex={0}
                >
                    {children}
                </a>
            );

            return {
                [labels.colSerial]: <Cell>{m.serial}</Cell>,
                [labels.colAddress]: <Cell>{m.address}</Cell>,
                [labels.colValidFrom]: <Cell>{desde}</Cell>,
                [labels.colValidTo]: <Cell>{hasta}</Cell>,
            };
        });
    }, [effectiveMeters, labels]);

    return (
        <div className="ami-page">
            {!showDetail && (
                <>
                    <HeaderSearch
                        data={ids}
                        setSearch={setSearch}
                        setSelected={setSelected}
                        inputSearch={search}
                        idClient={selected}
                    />

                    <section className="card" style={{ marginTop: 16 }}>
                        <div className="card-header">
                            <h2>{labels.pageTitle}</h2>
                        </div>

                        <div className="card-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            {loading && <p>{labels.loadingMeters}</p>}

                            {!loading && !error && currentArray.length === 0 && selected && (
                                <p>{labels.noMeters}</p>
                            )}

                            {!loading && currentArray.length > 0 && (
                                <AssociatedClientsTable
                                    props={{
                                        newArrTitle: titles,
                                        currentArray: currentArray,
                                    }}
                                />
                            )}
                        </div>
                    </section>
                </>
            )}

            {showDetail && (
                <AmiDetail
                    selected={selectedRow}
                    numeroCliente={selected}
                    onBack={handleBack}
                />
            )}
        </div>
    );
}
